import { clsx } from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Flex } from "@/components/Flex/Flex";
import { Text } from "@/components/Text";
import { TelegramIcon } from "./icons";

/** The QR is only useful to someone holding a SEPARATE device to scan with, so it
 *  is gated on a real pointer as well as a wide viewport. `min-width: 768px` alone
 *  matches iPad portrait and any phone in "request desktop site" — touch devices
 *  that should just tap the button. */
export const DESKTOP_QUERY = "(min-width: 768px) and (pointer: fine)";

/** Quiet zone in MODULES. The QR spec requires 4; less and phone cameras start
 *  failing to find the symbol against a busy background. Exported so the decode
 *  test measures the geometry this component actually paints. */
export const QUIET_MODULES = 4;

/** Rendered edge of the code in CSS px. Exported for the same reason: the module
 *  pitch a camera sees is `QR_PX / (modules + 2 * QUIET_MODULES)`, and
 *  `connect-qr-decode.test.ts` enforces a floor on it. */
export const QR_PX = 120;

export interface ConnectQrTarget {
  provider: "telegram" | "max";
  /** The personal bind URL this code encodes. */
  href: string;
  label: string;
}

export interface ConnectQrCodeData {
  target: ConnectQrTarget;
  matrix: boolean[][];
}

/** True once this is a desktop-with-a-mouse viewport. Starts false so SSR and the
 *  first client render agree (no hydration mismatch), then re-evaluates live —
 *  a maximised window must gain the codes, a narrowed one must lose them. */
export function useDesktopViewport(): boolean {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(DESKTOP_QUERY);
    const apply = () => setIsDesktop(mql.matches);
    apply();
    // Guarded: an old WebView can expose MediaQueryList without addEventListener,
    // and throwing here would take the whole effect — and the codes — down.
    if (typeof mql.addEventListener !== "function") return;
    mql.addEventListener("change", apply);
    return () => mql.removeEventListener("change", apply);
  }, []);
  return isDesktop;
}

/**
 * Encode every target, in one effect.
 *
 * The encoder is loaded with a DYNAMIC import so it lands in its own Rollup chunk
 * and is fetched only by the desktop visitors who can actually use a QR — 75 % of
 * traffic is mobile and would otherwise pay for a feature it can never use.
 *
 * 🛑 Encoding happens IN THE BROWSER on purpose. The string is the visitor's
 * personal bind credential (ADR-0223: one hash binds either messenger), and it is
 * already in this document as the button's `href`. Generating here transmits it
 * nowhere new. A server route would mint a second credential-bearing URL, and a
 * third-party QR image service would hand the credential to another host outright
 * — never do either.
 *
 * Returns ONLY codes that actually encoded, so the caller can distinguish "codes
 * on screen" from "codes intended". A failed chunk fetch (a stale hash across a
 * deploy, a blocking proxy) would otherwise leave blank plates *and* suppress the
 * fallback link the visitor had before this feature existed.
 */
export function useQrCodes(targets: ConnectQrTarget[]): ConnectQrCodeData[] {
  const [codes, setCodes] = useState<ConnectQrCodeData[]>([]);
  // `targets` is a fresh array each render; this is its stable identity.
  const key = targets.map((t) => `${t.provider}:${t.label}:${t.href}`).join("|");

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` IS the serialized identity of `targets`
  useEffect(() => {
    if (!key) {
      setCodes([]);
      return;
    }
    let stale = false;
    void import("uqr")
      .then(({ encode }) => {
        if (stale) return;
        setCodes(
          targets.map((target) => ({
            target,
            // `border: 0` — the quiet zone is drawn by the SVG below, so the
            // matrix stays pure modules and the padding lives in one place.
            matrix: encode(target.href, { border: 0 }).data,
          })),
        );
      })
      .catch(() => {
        // Never break the page: the buttons still work and the caller restores
        // its fallback link.
        if (!stale) setCodes([]);
      });
    return () => {
      stale = true;
    };
  }, [key]);

  return codes;
}

/** One code. Dark modules on a WHITE plate with a quiet zone — the card behind it
 *  is saturated brand purple, and a QR drawn straight onto that does not reliably
 *  scan. */
export function ConnectQrCode({
  code,
  plain = false,
}: {
  code: ConnectQrCodeData;
  plain?: boolean;
}) {
  const { target, matrix } = code;
  const span = matrix.length + QUIET_MODULES * 2;

  // Memoised: the parent re-renders on every progress-bar tween frame, and one
  // code is ~500-700 rects — rebuilding them 60x/s is pure waste.
  const rects = useMemo(
    () =>
      matrix.flatMap((row, y) =>
        row.map((on, x) =>
          on ? (
            <rect
              // biome-ignore lint/suspicious/noArrayIndexKey: a QR matrix is a fixed grid — the coordinates ARE the module's identity, and it never reorders
              key={`${x}-${y}`}
              x={x + QUIET_MODULES}
              y={y + QUIET_MODULES}
              width={1}
              height={1}
              // Functional, not decorative: a scanner needs maximum contrast
              // against the white plate, in every theme.
              fill="#000"
            />
          ) : null,
        ),
      ),
    [matrix],
  );

  return (
    <Flex
      as="figure"
      direction="column"
      align="center"
      gap={8}
      data-component="ConnectQrCode"
      data-qr-provider={target.provider}
      className="m-0"
    >
      {/* The plate is `bg-brand-content` — literally white (`--raw-base-100`) —
          and it supplies BOTH the light ground a scanner needs and the white
          behind the quiet zone, so the SVG itself paints no background. */}
      <Flex
        align="center"
        justify="center"
        className={clsx(
          "h-32 w-32 rounded-2xl bg-brand-content p-1",
          plain && "border border-border",
        )}
      >
        <svg
          viewBox={`0 0 ${span} ${span}`}
          width={QR_PX}
          height={QR_PX}
          shapeRendering="crispEdges"
          role="img"
          // The payload is a bind credential — it must never become readable
          // text. The visible caption below names the destination instead.
          aria-label={target.label}
        >
          <title>{target.label}</title>
          {rects}
        </svg>
      </Flex>
      <Flex
        as="figcaption"
        align="center"
        gap={4}
        className={clsx(
          "text-(length:--type-caption) font-(weight:--fw-medium)",
          plain ? "text-fg-soft" : "text-brand-content/85",
        )}
      >
        {target.provider === "telegram" ? (
          <>
            <TelegramIcon />
            {target.label}
          </>
        ) : (
          // The MAX mark IS the wordmark, so it stands alone — rendering the
          // label beside it read "MAX MAX".
          <span
            className={clsx(
              "rounded px-1.5 py-0.5 font-(weight:--fw-heavy)",
              plain ? "bg-brand/15" : "bg-brand-content/15",
            )}
          >
            {target.label}
          </span>
        )}
      </Flex>
    </Flex>
  );
}

ConnectQrCode.displayName = "ConnectQrCode";

/**
 * The desktop QR pair, rendered ABOVE the CTA stack.
 *
 * Placement is deliberate: the connect buttons cannot help a desktop visitor at
 * all (MAX's own web client, web.max.ru, logs in by QR only), so the codes must be
 * visible at the moment that visitor is stuck — not below three buttons that would
 * push them off a 1366×768 screen, and not behind a choice they cannot act on yet.
 */
export function ConnectQrCodes({
  codes,
  onScanned,
  plain = false,
  scanLabel = "Отсканировали код?",
  title = "Наведите камеру телефона — подключитесь там",
}: {
  codes: ConnectQrCodeData[];
  /** Match the parent connect card's surface — see `TelegramConnectSchema.surface`.
   *  Every foreground here is `brand-content` (white) for the purple block, so on
   *  a white card they must invert or the whole panel renders white-on-white. */
  plain?: boolean;
  /** The scan itself is invisible to us: a camera fires no click. Without this
   *  the visitor connects on their phone and the page gate never opens. */
  onScanned: () => void;
  scanLabel?: string;
  title?: string;
}) {
  if (codes.length === 0) return null;

  return (
    <div
      data-component="ConnectQrCodes"
      className={clsx("mt-4 rounded-2xl p-3", plain ? "bg-brand/5" : "bg-brand-content/10")}
    >
      <Text
        variant="body-sm"
        as="p"
        className={clsx("mb-2 text-center", plain ? "text-fg-soft" : "text-brand-content/85")}
      >
        {title}
      </Text>
      <Flex align="start" justify="center" gap={16} wrap>
        {codes.map((code) => (
          <ConnectQrCode key={code.target.provider} code={code} plain={plain} />
        ))}
      </Flex>
      <div className="mt-2 text-center">
        <button
          type="button"
          onClick={onScanned}
          data-track="lm_next.cta.qr_scanned"
          className={clsx(
            "text-(length:--type-body-sm) font-(weight:--fw-medium) underline",
            plain ? "text-muted" : "text-brand-content/80",
          )}
        >
          {scanLabel}
        </button>
      </div>
    </div>
  );
}

ConnectQrCodes.displayName = "ConnectQrCodes";
